/**
 * Pump.fun Bonding Curve Trading
 *
 * Direct integration with pump.fun's bonding curve program.
 * Same contract interactions as pump.fun frontend.
 */

import {
  Connection,
  PublicKey,
  TransactionInstruction,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
} from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddress,
} from '@solana/spl-token';

// Pump.fun Program IDs
export const PUMP_FUN_PROGRAM_ID = new PublicKey('6EF8rrecthR5Dkzon8Nwu78hRvfCKubJ14M5uBEwF6P');
export const PUMP_FUN_GLOBAL = new PublicKey('4wTV1YmiEkRvAtNtsSGPtUrqRYQMe5SKy2uB4Jjaxnjf');
export const PUMP_FUN_FEE_RECIPIENT = new PublicKey('CebN5WGQ4jvEPvsVU4EoHEpgzq1VV7AbicfhtW4xC9iM');
export const PUMP_FUN_EVENT_AUTHORITY = new PublicKey('Ce6TQqeHC9p8KetsN6JsjHK7UTZk7nasjjnr7XxXp9F1');

// Associated accounts
const SYSTEM_PROGRAM_ID = SystemProgram.programId;

/**
 * Bonding curve state - stored on-chain for each token
 */
export interface BondingCurveState {
  virtualTokenReserves: bigint;
  virtualSolReserves: bigint;
  realTokenReserves: bigint;
  realSolReserves: bigint;
  tokenTotalSupply: bigint;
  complete: boolean; // true if graduated to Raydium
}

/**
 * Derive the bonding curve PDA for a token mint
 */
export function getBondingCurvePDA(mint: PublicKey): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('bonding-curve'), mint.toBuffer()],
    PUMP_FUN_PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the bonding curve token account (holds the tokens for sale)
 */
export function getBondingCurveTokenAccount(mint: PublicKey): PublicKey {
  const bondingCurve = getBondingCurvePDA(mint);
  const [pda] = PublicKey.findProgramAddressSync(
    [bondingCurve.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  );
  return pda;
}

/**
 * Fetch and parse bonding curve state from chain
 */
export async function getBondingCurveState(
  connection: Connection,
  mint: PublicKey
): Promise<BondingCurveState | null> {
  const bondingCurve = getBondingCurvePDA(mint);
  const accountInfo = await connection.getAccountInfo(bondingCurve);

  if (!accountInfo || accountInfo.data.length < 49) {
    return null; // Not a pump.fun token or account doesn't exist
  }

  // Parse bonding curve data
  // Layout: discriminator (8) + virtualTokenReserves (8) + virtualSolReserves (8) +
  //         realTokenReserves (8) + realSolReserves (8) + tokenTotalSupply (8) + complete (1)
  const data = accountInfo.data;

  return {
    virtualTokenReserves: data.readBigUInt64LE(8),
    virtualSolReserves: data.readBigUInt64LE(16),
    realTokenReserves: data.readBigUInt64LE(24),
    realSolReserves: data.readBigUInt64LE(32),
    tokenTotalSupply: data.readBigUInt64LE(40),
    complete: data[48] === 1,
  };
}

/**
 * Calculate tokens received for a given SOL input (buy)
 * Uses constant product formula: x * y = k
 */
export function calculateBuyOutput(
  solAmount: bigint,
  state: BondingCurveState
): bigint {
  if (state.complete) {
    throw new Error('Token has graduated - use PumpSwap or Raydium');
  }

  // output = (input * reserveOut) / (reserveIn + input)
  const numerator = solAmount * state.virtualTokenReserves;
  const denominator = state.virtualSolReserves + solAmount;

  return numerator / denominator;
}

/**
 * Calculate SOL received for a given token input (sell)
 */
export function calculateSellOutput(
  tokenAmount: bigint,
  state: BondingCurveState
): bigint {
  if (state.complete) {
    throw new Error('Token has graduated - use PumpSwap or Raydium');
  }

  // output = (input * reserveOut) / (reserveIn + input)
  const numerator = tokenAmount * state.virtualSolReserves;
  const denominator = state.virtualTokenReserves + tokenAmount;

  return numerator / denominator;
}

/**
 * Apply slippage to get minimum acceptable output
 */
export function applySlippage(amount: bigint, slippageBps: number): bigint {
  // slippageBps: 100 = 1%, 500 = 5%
  const slippageMultiplier = BigInt(10000 - slippageBps);
  return (amount * slippageMultiplier) / BigInt(10000);
}

/**
 * Build pump.fun buy instruction
 */
export async function buildBuyInstruction(params: {
  mint: PublicKey;
  userWallet: PublicKey;
  solAmount: bigint; // in lamports
  minTokensOut: bigint; // minimum tokens to receive (slippage protected)
}): Promise<TransactionInstruction> {
  const { mint, userWallet, solAmount, minTokensOut } = params;

  const bondingCurve = getBondingCurvePDA(mint);
  const bondingCurveTokenAccount = getBondingCurveTokenAccount(mint);
  const userTokenAccount = await getAssociatedTokenAddress(mint, userWallet);

  // Buy instruction discriminator (from pump.fun IDL)
  const BUY_DISCRIMINATOR = Buffer.from([102, 6, 61, 18, 1, 218, 235, 234]);

  // Instruction data: discriminator + amount (u64) + maxSolCost (u64)
  const data = Buffer.alloc(8 + 8 + 8);
  BUY_DISCRIMINATOR.copy(data, 0);
  data.writeBigUInt64LE(minTokensOut, 8); // amount of tokens to buy
  data.writeBigUInt64LE(solAmount, 16); // max SOL to spend

  return new TransactionInstruction({
    programId: PUMP_FUN_PROGRAM_ID,
    keys: [
      { pubkey: PUMP_FUN_GLOBAL, isSigner: false, isWritable: false },
      { pubkey: PUMP_FUN_FEE_RECIPIENT, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: bondingCurve, isSigner: false, isWritable: true },
      { pubkey: bondingCurveTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userWallet, isSigner: true, isWritable: true },
      { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
      { pubkey: PUMP_FUN_EVENT_AUTHORITY, isSigner: false, isWritable: false },
      { pubkey: PUMP_FUN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/**
 * Build pump.fun sell instruction
 */
export async function buildSellInstruction(params: {
  mint: PublicKey;
  userWallet: PublicKey;
  tokenAmount: bigint; // tokens to sell
  minSolOut: bigint; // minimum SOL to receive (slippage protected)
}): Promise<TransactionInstruction> {
  const { mint, userWallet, tokenAmount, minSolOut } = params;

  const bondingCurve = getBondingCurvePDA(mint);
  const bondingCurveTokenAccount = getBondingCurveTokenAccount(mint);
  const userTokenAccount = await getAssociatedTokenAddress(mint, userWallet);

  // Sell instruction discriminator (from pump.fun IDL)
  const SELL_DISCRIMINATOR = Buffer.from([51, 230, 133, 164, 1, 127, 131, 173]);

  // Instruction data: discriminator + amount (u64) + minSolOutput (u64)
  const data = Buffer.alloc(8 + 8 + 8);
  SELL_DISCRIMINATOR.copy(data, 0);
  data.writeBigUInt64LE(tokenAmount, 8); // amount of tokens to sell
  data.writeBigUInt64LE(minSolOut, 16); // min SOL to receive

  return new TransactionInstruction({
    programId: PUMP_FUN_PROGRAM_ID,
    keys: [
      { pubkey: PUMP_FUN_GLOBAL, isSigner: false, isWritable: false },
      { pubkey: PUMP_FUN_FEE_RECIPIENT, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: bondingCurve, isSigner: false, isWritable: true },
      { pubkey: bondingCurveTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userWallet, isSigner: true, isWritable: true },
      { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: PUMP_FUN_EVENT_AUTHORITY, isSigner: false, isWritable: false },
      { pubkey: PUMP_FUN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/**
 * Get quote for a buy order
 */
export async function getBuyQuote(
  connection: Connection,
  mint: PublicKey,
  solAmount: bigint,
  slippageBps: number
): Promise<{
  expectedTokens: bigint;
  minTokensOut: bigint;
  priceImpact: number;
  isGraduated: boolean;
}> {
  const state = await getBondingCurveState(connection, mint);

  if (!state) {
    throw new Error('Token not found on pump.fun');
  }

  if (state.complete) {
    return {
      expectedTokens: BigInt(0),
      minTokensOut: BigInt(0),
      priceImpact: 0,
      isGraduated: true,
    };
  }

  const expectedTokens = calculateBuyOutput(solAmount, state);
  const minTokensOut = applySlippage(expectedTokens, slippageBps);

  // Calculate price impact
  // Price before = virtualSolReserves / virtualTokenReserves
  // Price after = (virtualSolReserves + solAmount) / (virtualTokenReserves - expectedTokens)
  const priceBefore = Number(state.virtualSolReserves) / Number(state.virtualTokenReserves);
  const priceAfter = Number(state.virtualSolReserves + solAmount) /
                     Number(state.virtualTokenReserves - expectedTokens);
  const priceImpact = ((priceAfter - priceBefore) / priceBefore) * 100;

  return {
    expectedTokens,
    minTokensOut,
    priceImpact,
    isGraduated: false,
  };
}

/**
 * Get quote for a sell order
 */
export async function getSellQuote(
  connection: Connection,
  mint: PublicKey,
  tokenAmount: bigint,
  slippageBps: number
): Promise<{
  expectedSol: bigint;
  minSolOut: bigint;
  priceImpact: number;
  isGraduated: boolean;
}> {
  const state = await getBondingCurveState(connection, mint);

  if (!state) {
    throw new Error('Token not found on pump.fun');
  }

  if (state.complete) {
    return {
      expectedSol: BigInt(0),
      minSolOut: BigInt(0),
      priceImpact: 0,
      isGraduated: true,
    };
  }

  const expectedSol = calculateSellOutput(tokenAmount, state);
  const minSolOut = applySlippage(expectedSol, slippageBps);

  // Calculate price impact
  const priceBefore = Number(state.virtualSolReserves) / Number(state.virtualTokenReserves);
  const priceAfter = Number(state.virtualSolReserves - expectedSol) /
                     Number(state.virtualTokenReserves + tokenAmount);
  const priceImpact = ((priceBefore - priceAfter) / priceBefore) * 100;

  return {
    expectedSol,
    minSolOut,
    priceImpact,
    isGraduated: false,
  };
}

/**
 * Check if a token is a pump.fun token
 */
export async function isPumpFunToken(
  connection: Connection,
  mint: PublicKey
): Promise<boolean> {
  const state = await getBondingCurveState(connection, mint);
  return state !== null;
}

/**
 * Check if a pump.fun token has graduated to Raydium/PumpSwap
 */
export async function isGraduated(
  connection: Connection,
  mint: PublicKey
): Promise<boolean> {
  const state = await getBondingCurveState(connection, mint);
  return state?.complete ?? false;
}
